import { useEffect, useMemo, useRef } from 'react';
import { AccessibilityInfo, ActivityIndicator, Platform, Pressable, ScrollView, View, findNodeHandle } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { ActiveTripDetails } from '@/components/trips/ActiveTripStatusCard';
import { Button } from '@/components/ui/Button';
import { Body, Caption, SubHeading } from '@/components/ui/Text';
import { useTheme } from '@/theme';
import type { Trip } from '@/services/trips';
import type { Booking } from '@/services/bookings';
import type { ItineraryItem } from '@/services/itinerary';
import type { DayBriefing } from '@/services/dayBriefing';
import { useTripItineraryDay } from '@/hooks/useTripItineraryDay';
import { getTripDayContext } from '@/utils/tripDayContext';
import { canUseGorhomBottomSheet } from '@/utils/runtimeGuards';

// Lazy load @gorhom/bottom-sheet because it requires Reanimated/native bits
// that are unavailable in Expo Go. When it fails to load, we fall back to a
// static panel UI so the screen still renders instead of crashing.
type BottomSheetModule = {
  default: any;
  BottomSheetView: any;
  BottomSheetScrollView?: any;
};

let cachedBottomSheet: BottomSheetModule | null | undefined;
const loadBottomSheet = (): BottomSheetModule | null => {
  if (cachedBottomSheet !== undefined) return cachedBottomSheet;

  if (!canUseGorhomBottomSheet()) {
    cachedBottomSheet = null;
    return null;
  }

  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const mod = require('@gorhom/bottom-sheet') as BottomSheetModule;
    cachedBottomSheet = mod;
    return mod;
  } catch {
    cachedBottomSheet = null;
    return null;
  }
};

type ActiveTripBottomSheetProps = {
  trip: Trip;
  nextItem?: ItineraryItem | null;
  booking?: Booking | null;
  snapPoints?: (string | number)[];
  onOpenItinerary?: (tripId: string) => void;
  onShareTrip?: (trip: Trip) => void;
  onDismiss?: (tripId: string) => void;
  onMinimize?: (tripId: string) => void;
  briefing?: DayBriefing | null;
};

const formatItemTime = (item: ItineraryItem) => {
  if (item.startTime) return item.startTime;
  const hours = item.startDate.getHours();
  const minutes = item.startDate.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const displayHour = hours % 12 || 12;
  const displayMinutes = minutes.toString().padStart(2, '0');
  return `${displayHour}:${displayMinutes} ${ampm}`;
};

export function ActiveTripBottomSheet({
  trip,
  nextItem,
  booking,
  snapPoints,
  onOpenItinerary,
  onShareTrip,
  onDismiss,
  onMinimize,
  briefing,
}: ActiveTripBottomSheetProps) {
  const bottomSheetModule = useMemo(() => loadBottomSheet(), []);
  const BottomSheetComponent = bottomSheetModule?.default;
  const BottomSheetViewComponent = bottomSheetModule?.BottomSheetView;
  const BottomSheetScrollViewComponent = bottomSheetModule?.BottomSheetScrollView;

  const bottomSheetRef = useRef<any>(null);
  const headingRef = useRef<View>(null);
  const pendingCloseAction = useRef<'dismiss' | 'minimize' | null>(null);
  const closeFallbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Gorhom can emit onChange(-1) during initial layout; skip until we've seen snap index ≥ 0 once. */
  const hasReachedOpenSnapRef = useRef(false);
  /** Prevent double finalize when both onChange(-1) and the fallback timer run. */
  const finalizeCloseHandledRef = useRef(false);
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const router = useRouter();
  const dayContext = getTripDayContext(trip);

  const { items: todayItems, loading, refresh, isStale } = useTripItineraryDay(trip.id, {
    trip,
    dateKey: dayContext.todayKey,
    timeZone: dayContext.timeZone,
  });

  const resolvedSnapPoints = useMemo(() => {
    if (snapPoints?.length) {
      return snapPoints;
    }
    if (Platform.OS === 'web') {
      return ['25%', '70%'];
    }
    return [120, '65%'];
  }, [snapPoints]);

  const handleOpenItinerary = () => {
    if (onOpenItinerary) {
      onOpenItinerary(trip.id);
      return;
    }
    router.push({
      pathname: '/(tabs)/plan',
      params: { tripId: trip.id },
    });
  };

  useEffect(() => {
    return () => {
      if (closeFallbackTimeoutRef.current) {
        clearTimeout(closeFallbackTimeoutRef.current);
        closeFallbackTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    hasReachedOpenSnapRef.current = false;
    finalizeCloseHandledRef.current = false;
  }, [trip.id]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const node = findNodeHandle(headingRef.current);
      if (node) {
        AccessibilityInfo.setAccessibilityFocus(node);
      }
    }, 80);
    return () => clearTimeout(timeout);
  }, [trip.id]);

  const clearCloseFallbackTimer = () => {
    if (closeFallbackTimeoutRef.current) {
      clearTimeout(closeFallbackTimeoutRef.current);
      closeFallbackTimeoutRef.current = null;
    }
  };

  /** v5 exposes onChange; there is no onClose prop. */
  const finalizeClosed = () => {
    clearCloseFallbackTimer();

    if (finalizeCloseHandledRef.current) {
      return;
    }
    finalizeCloseHandledRef.current = true;

    const action = pendingCloseAction.current;
    pendingCloseAction.current = null;

    if (action === 'minimize') {
      onMinimize?.(trip.id);
      return;
    }
    if (action === 'dismiss') {
      onDismiss?.(trip.id);
      return;
    }

    /** Pan-down close leaves pending unset; treat as dismiss. */
    onDismiss?.(trip.id);
  };

  const handleSheetIndexChange = (index: number) => {
    if (typeof index !== 'number') {
      return;
    }
    if (index >= 0) {
      hasReachedOpenSnapRef.current = true;
      finalizeCloseHandledRef.current = false;
      return;
    }

    if (!hasReachedOpenSnapRef.current) {
      return;
    }
    finalizeClosed();
  };

  const scheduleFinalizeFallback = () => {
    clearCloseFallbackTimer();
    closeFallbackTimeoutRef.current = setTimeout(finalizeClosed, 950);
  };

  const handleDismiss = () => {
    if (!onDismiss) return;
    pendingCloseAction.current = 'dismiss';
    bottomSheetRef.current?.close?.();
    if (!BottomSheetComponent) {
      onDismiss(trip.id);
      return;
    }
    scheduleFinalizeFallback();
  };

  const handleMinimize = () => {
    if (!onMinimize) return;
    pendingCloseAction.current = 'minimize';
    bottomSheetRef.current?.close?.();
    if (!BottomSheetComponent) {
      onMinimize(trip.id);
      return;
    }
    scheduleFinalizeFallback();
  };

  const content = (
    <View
      style={{
        paddingHorizontal: theme.spacing(2),
        paddingBottom: insets.bottom + theme.spacing(2),
        gap: theme.spacing(2),
      }}
    >
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <View
          ref={headingRef}
          style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}
          accessible
          accessibilityRole="header"
        >
          <Ionicons name="sparkles-outline" size={16} color={theme.colors.textSecondary} />
          <Caption color={theme.colors.textSecondary} style={{ fontSize: 12 }}>
            Active trip
          </Caption>
        </View>
        <View style={{ flexDirection: 'row', gap: theme.spacing(0.5) }}>
          {onMinimize && (
            <Pressable
              onPress={handleMinimize}
              style={{
                padding: theme.spacing(0.75),
                borderRadius: theme.radii.pill,
                backgroundColor: theme.colors.borderMuted + '40',
              }}
              accessibilityRole="button"
              accessibilityLabel="Minimize active trip card"
            >
              <Ionicons name="remove-outline" size={18} color={theme.colors.text} />
            </Pressable>
          )}
          {onDismiss && (
            <Pressable
              onPress={handleDismiss}
              style={{
                padding: theme.spacing(0.75),
                borderRadius: theme.radii.pill,
                backgroundColor: theme.colors.borderMuted + '40',
              }}
              accessibilityRole="button"
              accessibilityLabel="Close active trip card"
            >
              <Ionicons name="close" size={18} color={theme.colors.text} />
            </Pressable>
          )}
        </View>
      </View>

      <ActiveTripDetails
        trip={trip}
        nextItem={nextItem}
        booking={booking}
        briefing={briefing}
        onOpenItinerary={handleOpenItinerary}
        onShare={onShareTrip ? () => onShareTrip(trip) : undefined}
        variant="sheet"
        showActions={false}
      />

      <View
        style={{
          gap: theme.spacing(1),
          padding: theme.spacing(1.5),
          borderRadius: theme.radii.lg,
          backgroundColor: theme.colors.borderMuted + '25',
        }}
      >
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
          <SubHeading style={{ fontSize: 16, fontWeight: '700' }}>Today's itinerary</SubHeading>
          <Pressable
            onPress={refresh}
            disabled={loading}
            style={{
              paddingHorizontal: theme.spacing(1.5),
              paddingVertical: theme.spacing(0.5),
              borderRadius: theme.radii.pill,
              backgroundColor: theme.colors.surface,
              borderWidth: 1,
              borderColor: theme.colors.borderMuted,
              opacity: loading ? 0.5 : 1,
            }}
          >
            <Caption style={{ color: theme.colors.textSecondary, fontWeight: '600' }}>
              {loading ? 'Refreshing...' : isStale ? 'Refresh' : 'Up to date'}
            </Caption>
          </Pressable>
        </View>

        {loading ? (
          <View style={{ alignItems: 'center', paddingVertical: theme.spacing(2) }}>
            <ActivityIndicator color={theme.colors.primary} />
            <Caption style={{ marginTop: theme.spacing(1), color: theme.colors.textSecondary }}>
              Pulling the latest plan...
            </Caption>
          </View>
        ) : todayItems.length === 0 ? (
          <View style={{ alignItems: 'center', paddingVertical: theme.spacing(2), gap: theme.spacing(0.5) }}>
            <Ionicons name="sparkles-outline" size={24} color={theme.colors.textSecondary} />
            <Body color={theme.colors.textSecondary} style={{ textAlign: 'center' }}>
              Nothing scheduled yet. Add plans to unlock proactive reminders.
            </Body>
          </View>
        ) : (
          todayItems.map((item) => (
            <View
              key={item.id}
              style={{
                flexDirection: 'row',
                gap: theme.spacing(1.5),
                alignItems: 'flex-start',
                paddingVertical: theme.spacing(0.5),
              }}
            >
              <View
                style={{
                  paddingHorizontal: theme.spacing(1),
                  paddingVertical: theme.spacing(0.5),
                  borderRadius: theme.radii.md,
                  backgroundColor: theme.colors.surface,
                  borderWidth: 1,
                  borderColor: theme.colors.borderMuted,
                }}
              >
                <Caption style={{ fontWeight: '600' }}>{formatItemTime(item)}</Caption>
              </View>
              <View style={{ flex: 1, gap: theme.spacing(0.25) }}>
                <Body style={{ fontWeight: '600' }}>{item.title}</Body>
                {item.location && (
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
                    <Ionicons name="pin-outline" size={12} color={theme.colors.textSecondary} />
                    <Caption style={{ color: theme.colors.textSecondary }}>{item.location}</Caption>
                  </View>
                )}
              </View>
            </View>
          ))
        )}
      </View>

      <Button
        label="View full itinerary"
        onPress={handleOpenItinerary}
        variant="secondary"
        icon={<Ionicons name="open-outline" size={16} color={theme.colors.primary} />}
      />
    </View>
  );

  const scrollableContent = (
    <ScrollView
      showsVerticalScrollIndicator={false}
      contentContainerStyle={{
        paddingBottom: insets.bottom + theme.spacing(2),
      }}
    >
      {content}
    </ScrollView>
  );

  // If @gorhom/bottom-sheet failed to load (Expo Go), render a static panel.
  if (!BottomSheetComponent || !BottomSheetViewComponent) {
    return (
      <View
        style={{
          backgroundColor: theme.colors.surface,
          borderTopLeftRadius: theme.radii.xl,
          borderTopRightRadius: theme.radii.xl,
          paddingTop: theme.spacing(1.5),
          ...theme.shadows.level1,
        }}
      >
        {scrollableContent}
      </View>
    );
  }

  const sheetBody = BottomSheetScrollViewComponent ? (
    <BottomSheetScrollViewComponent
      showsVerticalScrollIndicator={false}
      contentContainerStyle={{
        paddingBottom: insets.bottom + theme.spacing(2),
      }}
    >
      {content}
    </BottomSheetScrollViewComponent>
  ) : (
    <BottomSheetViewComponent accessibilityViewIsModal>{scrollableContent}</BottomSheetViewComponent>
  );

  return (
    <BottomSheetComponent
      ref={bottomSheetRef}
      index={0}
      snapPoints={resolvedSnapPoints}
      enableDynamicSizing={false}
      animateOnMount={false}
      enablePanDownToClose={Boolean(onDismiss)}
      onChange={handleSheetIndexChange}
      backgroundStyle={{
        backgroundColor: theme.colors.surface,
        borderTopLeftRadius: theme.radii.xl,
        borderTopRightRadius: theme.radii.xl,
      }}
      handleIndicatorStyle={{ backgroundColor: theme.colors.borderMuted }}
      style={{
        shadowColor: '#000',
        shadowOffset: { width: 0, height: -4 },
        shadowOpacity: 0.1,
        shadowRadius: 12,
        elevation: 12,
      }}
    >
      {sheetBody}
    </BottomSheetComponent>
  );
}


